"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";

import { useRouter } from "next/navigation";
import axiosInstance from "@/hooks/axiosConfig";
import { User, GlobalData } from "@/types";

type ImpersonationActor = {
  userId: string;
  email?: string;
  userName?: string;
  roleName?: string;
  startedAt?: string;
};

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  impersonateUser: (targetUserId: string) => Promise<void>;
  revertImpersonation: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  isLoading: boolean;
  globalData: GlobalData;
  setGlobalData: (data: GlobalData) => void;
  refreshGlobalData: () => Promise<GlobalData | null>;
  error: Error | null;
  isSuperAdmin: boolean;
  isImpersonating: boolean;
  impersonationActor: ImpersonationActor | null;
}

const defaultGlobalData: GlobalData = {
  user: {
    _id: "",
    userName: "",
    onboardingCompleted: true,
    email: "",
    status: "",
    avatar: "",
    sellerProfile: [],
    logo: "",
    pageDefaults: {
      room: { occupancyLimit: 0, description: "" },
      guest: { occupancyLimit: 0 },
    },
    tableSettings: {},
    firstName: "",
    lastName: "",
    role: {
      name: "",
      displayName: "",
      level: 0,
      permissions: [],
    },
  },
  company: null,
  subscription: null,
  system: {
    modules: [],
  },
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const parseJwtPayload = (token: string | null): Record<string, any> | null => {
  if (!token) return null;
  try {
    const [, payload = ""] = token.split(".");
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch {
    return null;
  }
};

const extractImpersonationActor = (token: string | null): ImpersonationActor | null => {
  const payload = parseJwtPayload(token);
  const actor = payload?.impersonatedBy;
  if (!actor?.userId) return null;
  return actor as ImpersonationActor;
};

const isTenantRole = (roleName = "") => {
  const value = String(roleName || "").toLowerCase();
  return value === "buyer" || value === "supplier" || value.startsWith("buyer_") || value.startsWith("supplier_");
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [globalData, setGlobalData] = useState<GlobalData>(defaultGlobalData);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [impersonationActor, setImpersonationActor] = useState<ImpersonationActor | null>(null);

  const router = useRouter();

  const syncAuthTokens = useCallback((accessToken: string, refreshToken: string) => {
    localStorage.setItem("accessToken", accessToken);
    localStorage.setItem("refreshToken", refreshToken);
    setImpersonationActor(extractImpersonationActor(accessToken));
  }, []);

  const clearAuthTokens = useCallback(() => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setImpersonationActor(null);
  }, []);

  const fetchGlobalData = useCallback(async (): Promise<GlobalData> => {
    const token = localStorage.getItem("accessToken");
    if (!token) return defaultGlobalData;

    const response = await axiosInstance.get<GlobalData>("/globalData");
    if (!response.data) throw new Error("No data received from server");

    const isValidGlobalData = (data: any): data is GlobalData =>
      data && "user" in data && "company" in data;

    if (!isValidGlobalData(response.data)) {
      throw new Error("Invalid data format received from server");
    }

    return response.data;
  }, []);

  const refreshGlobalData = useCallback(async (): Promise<GlobalData | null> => {
    try {
      setIsLoading(true);
      setError(null);

      const data = await fetchGlobalData(); // includes user.modules from backend

      setGlobalData({
        ...data,
        system: {
          modules: data.system?.modules || [],
        },
      });

      setUser(data.user);
      return data;
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch global data")
      );
      setGlobalData(defaultGlobalData);
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchGlobalData]);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("accessToken");

      if (!token) {
        setIsLoading(false);
        return;
      }
      setImpersonationActor(extractImpersonationActor(token));

      try {
        const response = await axiosInstance.get("/auth/verifyToken");

        if (response.data.valid) {
          await refreshGlobalData();
        } else {
          clearAuthTokens();
          setUser(null);
          setGlobalData(defaultGlobalData);
        }
      } catch (error) {
        clearAuthTokens();
        setUser(null);
        setGlobalData(defaultGlobalData);
      } finally {
        setIsLoading(false);
      }
    };

    verifyToken();
  }, [refreshGlobalData, clearAuthTokens]);

  const login = async (email: string, password: string) => {
    try {
      const { data } = await axiosInstance.post("/auth/login", {
        email,
        password,
      });

      syncAuthTokens(data.accessToken, data.refreshToken);
      await refreshGlobalData();
    } catch (error) {
      throw error;
    }
  };

  const impersonateUser = async (targetUserId: string) => {
    const { data } = await axiosInstance.post(`/auth/impersonate/${targetUserId}`);
    syncAuthTokens(data.accessToken, data.refreshToken);
    if (data.user) {
      setUser(data.user);
    }
    await refreshGlobalData();
    router.refresh();
  };

  const revertImpersonation = async () => {
    const { data } = await axiosInstance.post("/auth/impersonate/revert");
    syncAuthTokens(data.accessToken, data.refreshToken);
    if (data.user) {
      setUser(data.user);
    }
    await refreshGlobalData();
    router.refresh();
  };

  const logout = () => {
    clearAuthTokens();
    setUser(null);
    setGlobalData(defaultGlobalData);
    router.push("/auth/login");
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;

    const userPermissions = user.role.permissions.map((p) => p.name);
    if (userPermissions.includes("*")) return true;

    return userPermissions.includes(permission);
  };

  const hasRole = (role: string): boolean => user?.role.name === role;
  const isSuperAdmin = !!user?.role?.permissions?.some((p) => p.name === "*");
  const isImpersonating = Boolean(impersonationActor?.userId);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        impersonateUser,
        revertImpersonation,
        hasPermission,
        hasRole,
        isLoading,
        globalData,
        setGlobalData,
        refreshGlobalData,
        error,
        isSuperAdmin,
        isImpersonating,
        impersonationActor,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
